import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, Save } from 'lucide-react';

export default function TransactionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);

  const [wallets, setWallets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    type: searchParams.get('type') || 'expense',
    amount: '',
    currency: 'AOA',
    description: '',
    date: new Date().toISOString().split('T')[0],
    category_id: '',
    wallet_id: '',
    to_wallet_id: '',
    notes: '',
    is_pending: false,
  });

  useEffect(() => {
    api.get('/wallets').then(r => setWallets(r.data));
    api.get('/categories').then(r => setCategories(r.data));
    if (isEditing) {
      api.get(`/transactions/${id}`).then(r => setForm({ ...r.data, amount: r.data.amount.toString() }));
    }
  }, [id]);

  const filteredCategories = categories.filter(c =>
    form.type === 'transfer' ? true : c.type === form.type || c.type === 'both'
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditing) {
        await api.put(`/transactions/${id}`, { ...form, amount: parseFloat(form.amount) });
      } else {
        await api.post('/transactions', { ...form, amount: parseFloat(form.amount) });
      }
      navigate('/transactions');
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao guardar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
          <h1 className="page-title">{isEditing ? 'Editar Transação' : 'Nova Transação'}</h1>
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Tipo */}
          <div className="tab-bar">
            {['expense', 'income', 'transfer'].map(t => (
              <button key={t} type="button" className={`tab-item ${form.type === t ? 'active' : ''}`}
                style={{ flex: 1 }} onClick={() => setForm({ ...form, type: t })}>
                {{ expense: '💸 Despesa', income: '💰 Receita', transfer: '🔄 Transferência' }[t]}
              </button>
            ))}
          </div>

          {/* Valor */}
          <div className="form-group" style={{ textAlign: 'center' }}>
            <label className="form-label">Valor</label>
            <input className="form-input form-input-money" style={{ textAlign: 'center', fontSize: 32, padding: '16px 20px' }}
              type="number" step="0.01" min="0" placeholder="0,00" value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })} required autoFocus />
          </div>

          {/* Descrição */}
          <div className="form-group">
            <label className="form-label">Descrição</label>
            <input className="form-input" value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ex: Supermercado Kero" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Data</label>
              <input className="form-input" type="date" value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })} required />
            </div>

            {form.type !== 'transfer' && (
              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select className="form-input form-select" value={form.category_id}
                  onChange={e => setForm({ ...form, category_id: e.target.value })} required>
                  <option value="">Selecionar...</option>
                  {filteredCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{form.type === 'transfer' ? 'De (Carteira)' : 'Carteira'}</label>
              <select className="form-input form-select" value={form.wallet_id}
                onChange={e => setForm({ ...form, wallet_id: e.target.value })} required>
                <option value="">Selecionar...</option>
                {wallets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>

            {form.type === 'transfer' && (
              <div className="form-group">
                <label className="form-label">Para (Carteira)</label>
                <select className="form-input form-select" value={form.to_wallet_id}
                  onChange={e => setForm({ ...form, to_wallet_id: e.target.value })} required>
                  <option value="">Selecionar...</option>
                  {wallets.filter(w => w.id !== parseInt(form.wallet_id)).map(w =>
                    <option key={w.id} value={w.id}>{w.name}</option>
                  )}
                </select>
              </div>
            )}
          </div>

          {/* Notas */}
          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea className="form-input" rows={2} value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notas adicionais..." />
          </div>

          {/* Pendente */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_pending}
              onChange={e => setForm({ ...form, is_pending: e.target.checked })} />
            Marcar como pendente
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => navigate(-1)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
              <Save size={16} /> {loading ? 'A guardar...' : isEditing ? 'Guardar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
